import React, { useState } from 'react';

// Define types for our component
interface SqlToMongoDBProps { }

// Define database types
type DatabaseTable = {
    name: string;
    documents: Record<string, any>[];
};

// Sample MongoDB collections for demo purposes
const mockMongoDB: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        documents: [
            { _id: '1', name: 'John Doe', email: 'john@example.com', created_at: new Date('2023-01-15') },
            { _id: '2', name: 'Jane Smith', email: 'jane@example.com', created_at: new Date('2023-02-20') },
            { _id: '3', name: 'Bob Johnson', email: 'bob@example.com', created_at: new Date('2023-03-10') },
            { _id: '4', name: 'Alice Brown', email: 'alice@example.com', created_at: new Date('2023-04-05') },
            { _id: '5', name: 'Charlie Davis', email: 'charlie@example.com', created_at: new Date('2023-05-12') }
        ]
    },
    products: {
        name: 'products',
        documents: [
            { _id: '101', name: 'Laptop', price: 1299.99, category: 'Electronics', in_stock: true },
            { _id: '102', name: 'Smartphone', price: 899.99, category: 'Electronics', in_stock: true },
            { _id: '103', name: 'Headphones', price: 199.99, category: 'Electronics', in_stock: false },
            { _id: '104', name: 'Desk Chair', price: 249.99, category: 'Furniture', in_stock: true },
            { _id: '105', name: 'Coffee Table', price: 349.99, category: 'Furniture', in_stock: true },
            { _id: '106', name: 'Bookshelf', price: 199.99, category: 'Furniture', in_stock: false },
            { _id: '107', name: 'Water Bottle', price: 24.99, category: 'Kitchen', in_stock: true },
            { _id: '108', name: 'Blender', price: 89.99, category: 'Kitchen', in_stock: true }
        ]
    },
    orders: {
        name: 'orders',
        documents: [
            { _id: '1001', user_id: '1', total: 1299.99, status: 'Delivered', order_date: new Date('2023-06-15') },
            { _id: '1002', user_id: '2', total: 899.99, status: 'Shipped', order_date: new Date('2023-06-20') },
            { _id: '1003', user_id: '3', total: 249.99, status: 'Processing', order_date: new Date('2023-06-25') },
            { _id: '1004', user_id: '1', total: 89.99, status: 'Delivered', order_date: new Date('2023-07-01') },
            { _id: '1005', user_id: '4', total: 549.98, status: 'Shipped', order_date: new Date('2023-07-05') },
            { _id: '1006', user_id: '5', total: 199.99, status: 'Processing', order_date: new Date('2023-07-10') }
        ]
    }
};

// SQL to MongoDB Parser
const sqlToMongoDB = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
    sqlQuery = sqlQuery.trim();

    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // Parse SQL query components
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const collectionName = selectMatch[2].trim();
        const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
        const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
        const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;

        // Check if collection exists
        if (!database[collectionName]) {
            throw new Error(`Collection '${collectionName}' does not exist`);
        }

        const collection = database[collectionName];

        // Generate MongoDB projection (fields to include)
        let projection: Record<string, number> = {};
        
        if (columns !== '*') {
            // For specific columns, set each to 1 (include)
            columns.split(',').forEach(col => {
                const colName = col.trim();
                projection[colName] = 1;
            });
        }

        // Generate MongoDB filter (where clause)
        let filter: Record<string, any> = {};

        if (whereClause) {
                            // Parse the WHERE clause to build MongoDB filter
            // This is a simplified version - production code would need more robust parsing
            const whereMatch = whereClause.match(/(\w+)\s*(=|!=|>|<|>=|<=)\s*([^;]*)/);

            if (whereMatch) {
                const field = whereMatch[1].trim();
                const operator = whereMatch[2].trim();
                let value: string | number | boolean = whereMatch[3].trim();

                // Remove quotes from string values
                if ((value.startsWith("'") && value.endsWith("'")) || 
                    (value.startsWith('"') && value.endsWith('"'))) {
                    value = value.substring(1, value.length - 1);
                }
                
                // Convert to appropriate type if needed
                let typedValue: string | number | boolean = value;
                if (value === 'true') typedValue = true;
                else if (value === 'false') typedValue = false;
                else if (!isNaN(Number(value)) && value !== '') typedValue = Number(value);

                // Map SQL operators to MongoDB operators
                switch (operator) {
                    case '=':
                        filter[field] = typedValue;
                        break;
                    case '!=':
                        filter[field] = { $ne: typedValue };
                        break;
                    case '>':
                        filter[field] = { $gt: typedValue };
                        break;
                    case '<':
                        filter[field] = { $lt: typedValue };
                        break;
                    case '>=':
                        filter[field] = { $gte: typedValue };
                        break;
                    case '<=':
                        filter[field] = { $lte: typedValue };
                        break;
                }
            } else {
                throw new Error('Invalid WHERE clause format. Only basic comparison operators are supported.');
            }
        }

        // Generate MongoDB sort (orderBy clause)
        let sort: Record<string, number> = {};

        if (orderByClause) {
            const orderParts = orderByClause.split(' ');
            const orderField = orderParts[0].trim();
            const isDesc = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc';
            
            sort[orderField] = isDesc ? -1 : 1;
        }

        // Execute MongoDB query
        let results = [...collection.documents];

        // Apply filter (WHERE)
        if (Object.keys(filter).length > 0) {
            results = results.filter(doc => {
                return Object.entries(filter).every(([field, condition]) => {
                    if (typeof condition === 'object' && condition !== null) {
                        // Handle operators like $gt, $lt, etc.
                        for (const [op, val] of Object.entries(condition)) {
                            const documentValue = doc[field];
                            const comparisonValue = val as any; // Type assertion for comparison operations
                            
                            switch (op) {
                                case '$ne': return documentValue !== comparisonValue;
                                case '$gt': return documentValue > comparisonValue;
                                case '$lt': return documentValue < comparisonValue;
                                case '$gte': return documentValue >= comparisonValue;
                                case '$lte': return documentValue <= comparisonValue;
                                default: return false;
                            }
                        }
                    } else {
                        // Simple equality
                        return doc[field] === condition;
                    }
                });
            });
        }

        // Apply sort (ORDER BY)
        if (Object.keys(sort).length > 0) {
            const [sortField, sortOrder] = Object.entries(sort)[0];
            results.sort((a, b) => {
                if (a[sortField] < b[sortField]) return -1 * sortOrder;
                if (a[sortField] > b[sortField]) return 1 * sortOrder;
                return 0;
            });
        }

        // Apply limit
        if (limitClause !== null && !isNaN(limitClause)) {
            results = results.slice(0, limitClause);
        }

        // Apply projection (SELECT columns)
        if (Object.keys(projection).length > 0) {
            results = results.map(doc => {
                const projectedDoc: Record<string, any> = {};
                Object.keys(projection).forEach(field => {
                    projectedDoc[field] = doc[field];
                });
                return projectedDoc;
            });
        }

        // Generate MongoDB query object
        const mongoQuery = {
            find: collectionName,
            filter: filter,
            projection: Object.keys(projection).length > 0 ? projection : undefined,
            sort: Object.keys(sort).length > 0 ? sort : undefined,
            limit: limitClause !== null ? limitClause : undefined
        };

        // Return the MongoDB query and the results
        return {
            mongoQuery,
            results
        };
    } else {
        throw new Error('Only SELECT queries are supported for conversion to MongoDB');
    }
};

// Format MongoDB query to display as code
const formatMongoDBQuery = (mongoQuery: any): string => {
    // Build a MongoDB query string/code snippet
    let query = `db.${mongoQuery.find}.find(`;
    
    // Add filter
    if (Object.keys(mongoQuery.filter).length > 0) {
        query += JSON.stringify(mongoQuery.filter, null, 2);
    } else {
        query += '{}';
    }
    
    // Add projection if present
    if (mongoQuery.projection) {
        query += ', ' + JSON.stringify(mongoQuery.projection, null, 2);
    }
    
    query += ')';
    
    // Add sort if present
    if (mongoQuery.sort) {
        query += `.sort(${JSON.stringify(mongoQuery.sort, null, 2)})`;
    }
    
    // Add limit if present
    if (mongoQuery.limit) {
        query += `.limit(${mongoQuery.limit})`;
    }
    
    return query;
};

// Format MongoDB query as Node.js/TypeScript code
const formatAsJsCode = (mongoQuery: any): string => {
    let code = `// Using MongoDB Node.js Driver\n`;
    code += `const { MongoClient } = require('mongodb');\n\n`;
    code += `async function executeQuery() {\n`;
    code += `  const client = new MongoClient(process.env.MONGODB_URI);\n\n`;
    code += `  try {\n`;
    code += `    await client.connect();\n`;
    code += `    const database = client.db("your_database_name");\n`;
    code += `    const collection = database.collection("${mongoQuery.find}");\n\n`;
    
    code += `    const query = `;
    code += JSON.stringify(mongoQuery.filter, null, 4) || '{}';
    code += `;\n\n`;
    
    code += `    const options = {\n`;
    if (mongoQuery.projection) {
        code += `      projection: ${JSON.stringify(mongoQuery.projection, null, 6)},\n`;
    }
    if (mongoQuery.sort) {
        code += `      sort: ${JSON.stringify(mongoQuery.sort, null, 6)},\n`;
    }
    if (mongoQuery.limit) {
        code += `      limit: ${mongoQuery.limit},\n`;
    }
    code += `    };\n\n`;
    
    code += `    const cursor = collection.find(query, options);\n`;
    code += `    const results = await cursor.toArray();\n`;
    code += `    console.log(results);\n`;
    code += `  } finally {\n`;
    code += `    await client.close();\n`;
    code += `  }\n`;
    code += `}\n\n`;
    code += `executeQuery().catch(console.error);\n`;
    
    return code;
};

// Format MongoDB query as Python code
const formatAsPythonCode = (mongoQuery: any): string => {
    let code = `# Using PyMongo\n`;
    code += `from pymongo import MongoClient\n`;
    code += `import os\n\n`;
    code += `def execute_query():\n`;
    code += `    client = MongoClient(os.environ.get("MONGODB_URI"))\n`;
    code += `    db = client["your_database_name"]\n`;
    code += `    collection = db["${mongoQuery.find}"]\n\n`;
    
    code += `    query = `;
    code += JSON.stringify(mongoQuery.filter, null, 4).replace(/"/g, "'") || '{}';
    code += `\n\n`;
    
    let options = [];
    if (mongoQuery.projection) {
        options.push(`    projection = ${JSON.stringify(mongoQuery.projection, null, 4).replace(/"/g, "'")}`);
    }
    if (mongoQuery.sort) {
        options.push(`    sort = ${JSON.stringify(mongoQuery.sort, null, 4).replace(/"/g, "'")}`);
    }
    
    if (options.length > 0) {
        code += options.join('\n') + '\n\n';
    }
    
    code += `    cursor = collection.find(query`;
    if (mongoQuery.projection) {
        code += `, projection`;
    }
    code += `)\n`;
    
    if (mongoQuery.sort) {
        code += `    cursor = cursor.sort(sort)\n`;
    }
    
    if (mongoQuery.limit) {
        code += `    cursor = cursor.limit(${mongoQuery.limit})\n`;
    }
    
    code += `\n    results = list(cursor)\n`;
    code += `    for result in results:\n`;
    code += `        print(result)\n\n`;
    code += `if __name__ == "__main__":\n`;
    code += `    execute_query()\n`;
    
    return code;
};

// Main component to convert SQL to MongoDB
const SqlToMongoDB: React.FC<SqlToMongoDBProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the MongoDB query output
    const [mongodbOutput, setMongodbOutput] = useState<string>('');
    // State for the Node.js/TypeScript code
    const [jsCode, setJsCode] = useState<string>('');
    // State for the Python code
    const [pythonCode, setPythonCode] = useState<string>('');
    // State for the query results
    const [queryResults, setQueryResults] = useState<any[] | null>(null);
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isConverted, setIsConverted] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<string | null>(null);
    
    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM products WHERE category = "Electronics";';
        setSqlInput(exampleQuery);
        setError('');
        setIsConverted(false);
        setMongodbOutput('');
        setJsCode('');
        setPythonCode('');
        setQueryResults(null);
    };

    // Convert SQL to MongoDB
    const convertSqlToMongoDB = () => {
        setError('');
        setIsLoading(true);
        setIsConverted(false);
        setMongodbOutput('');
        setJsCode('');
        setPythonCode('');
        setQueryResults(null);

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            // Parse the SQL query and convert to MongoDB
            const { mongoQuery, results } = sqlToMongoDB(sqlInput, mockMongoDB);

            // Format the MongoDB query for display
            const formattedQuery = formatMongoDBQuery(mongoQuery);
            setMongodbOutput(formattedQuery);

            // Generate Node.js code
            setJsCode(formatAsJsCode(mongoQuery));

            // Generate Python code
            setPythonCode(formatAsPythonCode(mongoQuery));

            // Set the query results
            setQueryResults(results);
            
            setIsConverted(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error converting SQL query: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setMongodbOutput('');
        setJsCode('');
        setPythonCode('');
        setQueryResults(null);
        setError('');
        setIsConverted(false);
    };

    // Copy text to clipboard
    const copyToClipboard = (text: string, type: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopied(type);
                setTimeout(() => setCopied(null), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">MongoDB Collections Schema</h3>
                {Object.values(mockMongoDB).map(collection => (
                    <div key={collection.name} className="mb-3">
                        <div className="font-medium">{collection.name}</div>
                        <div className="pl-4">
                            {collection.documents.length > 0 && (
                                <>
                                    <div className="text-xs font-medium mt-1">Sample Document Structure:</div>
                                    <pre className="text-xs bg-blue-100 p-2 rounded mt-1">
                                        {JSON.stringify(collection.documents[0], null, 2)}
                                    </pre>
                                </>
                            )}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock MongoDB database for demonstration. In a real application, you would
                    connect to an actual MongoDB database.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to MongoDB Query Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert SQL queries to MongoDB queries. This tool helps you transform your SQL knowledge to MongoDB's document-based query language.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleQuery}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={(e) => {
                                setSqlInput(e.target.value);
                                setError('');
                                setIsConverted(false);
                            }}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE created_at > '2023-01-01';"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex justify-between">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            onClick={convertSqlToMongoDB}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Converting...
                                </div>
                            ) : (
                                'Convert to MongoDB'
                            )}
                        </button>

                        <button
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            onClick={clearAll}
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Output Section - Tabs for different outputs */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                            <p className="font-medium">Error</p>
                            <p>{error}</p>
                        </div>
                    )}

                    {!error && !isConverted && (
                        <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                            <div className="text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p>
                                    MongoDB queries will appear here after converting the SQL query.
                                </p>
                            </div>
                        </div>
                    )}

                    {!error && isConverted && (
                        <div className="flex-grow flex flex-col">
                            {/* Tabs */}
                            <div className="border-b border-gray-200">
                                <nav className="-mb-px flex" aria-label="Tabs">
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${mongodbOutput && 'border-blue-500 text-blue-600'}`}
                                        onClick={() => {
                                            // Simply show the MongoDB query tab
                                        }}
                                    >
                                        MongoDB Query
                                    </button>
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${jsCode && 'hover:text-gray-700'}`}
                                        onClick={() => {
                                            // Simply show the Node.js tab
                                        }}
                                    >
                                        Node.js Code
                                    </button>
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${pythonCode && 'hover:text-gray-700'}`}
                                        onClick={() => {
                                            // Simply show the Python tab
                                        }}
                                    >
                                        Python Code
                                    </button>
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${queryResults && 'hover:text-gray-700'}`}
                                        onClick={() => {
                                            // Simply show the Query Results tab
                                        }}
                                    >
                                        Results
                                    </button>
                                </nav>
                            </div>

                            {/* MongoDB Query Output */}
                            <div className="mt-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-medium">MongoDB Query</h3>
                                    <button
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        onClick={() => copyToClipboard(mongodbOutput, 'mongodb')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {copied === 'nodejs' ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto text-sm font-mono h-48">
                                    {jsCode}
                                </pre>
                            </div>

                            {/* Python Code */}
                            <div className="mt-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-medium">Python Code</h3>
                                    <button
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        onClick={() => copyToClipboard(pythonCode, 'python')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {copied === 'python' ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto text-sm font-mono h-48">
                                    {pythonCode}
                                </pre>
                            </div>

                            {/* Query Results */}
                            <div className="mt-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-medium">Query Results (Preview)</h3>
                                    <span className="text-sm text-gray-500">
                                        {queryResults?.length} document(s) found
                                    </span>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto h-48">
                                    {queryResults && queryResults.length > 0 ? (
                                        <pre className="text-sm font-mono">
                                            {JSON.stringify(queryResults, null, 2)}
                                        </pre>
                                    ) : (
                                        <div className="text-gray-500 text-center py-4">
                                            No results found
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {isConverted && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">SQL Query Successfully Converted to MongoDB!</p>
                            </div>
                            <p className="text-sm">
                                You can copy the MongoDB query or use the generated Node.js/TypeScript or Python code in your application.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">SQL to MongoDB Query Translation Guide</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Understanding the Conversion</h3>
                        <p className="text-gray-600">
                            This tool helps you convert SQL queries to MongoDB's query language. While SQL uses a structured, tabular approach, MongoDB uses a document-based model with collections instead of tables and documents instead of rows.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">SQL to MongoDB Mapping</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SQL Concept</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MongoDB Equivalent</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Example</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200 text-sm">
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">Table</td>
                                        <td className="px-6 py-4 whitespace-nowrap">Collection</td>
                                        <td className="px-6 py-4">users table → users collection</td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">Row</td>
                                        <td className="px-6 py-4 whitespace-nowrap">Document</td>
                                        <td className="px-6 py-4">Row in users → Document in users</td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">Column</td>
                                        <td className="px-6 py-4 whitespace-nowrap">Field</td>
                                        <td className="px-6 py-4">name column → name field</td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">LIMIT</td>
                                        <td className="px-6 py-4 whitespace-nowrap">limit()</td>
                                        <td className="px-6 py-4">LIMIT 10 → limit(10)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SQL Query Parsing</strong> - Parse basic SQL SELECT statements</li>
                            <li><strong>MongoDB Query Generation</strong> - Convert to MongoDB's query format</li>
                            <li><strong>Code Generation</strong> - Create ready-to-use Node.js/TypeScript and Python code</li>
                            <li><strong>Results Preview</strong> - View the query results against mock data</li>
                            <li><strong>Multiple Output Formats</strong> - See both raw MongoDB query and programming language implementations</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory MongoDB database with predefined documents</li>
                            <li>Only supports basic SELECT queries with simple WHERE clauses</li>
                            <li>Limited SQL syntax support (no JOINs, GROUP BY, HAVING, etc.)</li>
                            <li>Advanced MongoDB features like aggregation pipeline are not implemented</li>
                            <li>Doesn't support complex data types or nested document queries</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SELECT columns</strong> - Translated to MongoDB projection</li>
                            <li><strong>FROM table</strong> - Translated to MongoDB collection</li>
                            <li><strong>WHERE condition</strong> - Translated to MongoDB query filter</li>
                            <li><strong>ORDER BY column</strong> - Translated to MongoDB sort</li>
                            <li><strong>LIMIT number</strong> - Translated to MongoDB limit</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">MongoDB Resources</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><a href="https://www.mongodb.com/docs/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">MongoDB Documentation</a></li>
                            <li><a href="https://www.mongodb.com/docs/manual/tutorial/query-documents/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">MongoDB Query Documents</a></li>
                            <li><a href="https://www.mongodb.com/docs/drivers/node/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">MongoDB Node.js Driver</a></li>
                            <li><a href="https://www.mongodb.com/docs/drivers/pymongo/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">MongoDB Python Driver (PyMongo)</a></li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToMongoDB;